import { SortableContext } from "@dnd-kit/sortable";
import type { BlockRendererMapping } from "@circle-react-shared/BuilderUI/BuilderUI.types";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import { DroppableArea } from "@circle-react-shared/BuilderUI/DragAndDrop/DroppableArea";
import { useBuilderStore } from "@circle-react-shared/BuilderUI/Hooks/useBuilderStore";
import type { BlockCategoryName } from "@circle-react-shared/BuilderUI/constants/BlockCategories";
import { DroppablePreview } from "./DroppablePreview";
import { Preview } from "./Preview";
import { SortablePreview } from "./SortablePreview";

export interface PreviewBlockListProps {
  accepts: BlockCategoryName[];
  blocks: BaseBlock[];
  components: BlockRendererMapping;
  path: string;
  rejects: BlockCategoryName[];
}

export const PreviewBlockList = ({
  blocks,
  components,
  path,
  accepts,
  rejects,
}: PreviewBlockListProps) => {
  const isDraggingEnabled = useBuilderStore(state => state.isDraggingEnabled);
  const isSortingEnabled = useBuilderStore(state => state.isSortingEnabled);
  const isFloatingDropZonesEnabled = useBuilderStore(
    state => state.isFloatingDropZonesEnabled,
  );
  const childrenArrayPath = path
    .replace(/^blocks\./, "")
    .replace(/\.value+$/, "");

  if (isDraggingEnabled && isSortingEnabled) {
    return (
      <SortableContext items={blocks.map(block => block.id)}>
        {blocks.map((block, index) => (
          <SortablePreview
            key={block.id}
            block={block}
            components={components}
            path={`${path}.${index}`}
          />
        ))}
      </SortableContext>
    );
  }

  if (isDraggingEnabled && isFloatingDropZonesEnabled) {
    // NOTE: This is a "ghost" container. The `display: contents` style ensures
    // that this div does not generate a box in the layout, so it doesn't
    // interfere with parent flex or grid layouts. However, it still exists
    // in the DOM, allowing us to use it as a reliable measurement anchor.
    // https://caniuse.com/css-display-contents
    return (
      <div
        style={{ display: "contents" }}
        data-block-list-container="true"
        data-parent-path={childrenArrayPath}
        data-accepts={JSON.stringify(accepts)}
        data-rejects={JSON.stringify(rejects)}
      >
        {blocks.map((block, index) => (
          <Preview
            key={block.id}
            block={block}
            components={components}
            path={`${path}.${index}`}
            data-measurable-block="true"
            data-parent-path={childrenArrayPath}
            data-block-index={index}
            data-accepts={JSON.stringify(accepts)}
            data-rejects={JSON.stringify(rejects)}
          />
        ))}
      </div>
    );
  }

  if (isDraggingEnabled) {
    return (
      <>
        {blocks.length === 0 && (
          <DroppableArea
            path={childrenArrayPath}
            accepts={accepts}
            rejects={rejects}
            index={blocks.length}
          />
        )}
        {blocks.map((block, index) => (
          <DroppablePreview
            key={block.id}
            block={block}
            components={components}
            path={`${path}.${index}`}
            childrenArrayPath={childrenArrayPath}
            index={index}
            accepts={accepts}
            rejects={rejects}
          />
        ))}
      </>
    );
  }

  return (
    <>
      {blocks.map((block, index) => (
        <Preview
          key={block.id}
          block={block}
          components={components}
          path={`${path}.${index}`}
        />
      ))}
    </>
  );
};
